import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';

interface ClassifiedError {
  message: string;
  kind: string;
}

interface OrderError {
  orderNumber: string;
  rowNumbers: number[];
  errors: ClassifiedError[];
}

interface OrderLineError {
  orderNumber: string;
  orderLineNumber: string | null;
  productCode: string | null;
  rowNumber: number;
  errors: ClassifiedError[];
}

interface ImportIssue {
  column: string;
  problem: string;
  kind: string;
  rowCount: number;
  rows: number[];
}

interface Props {
  payload: Record<string, unknown> | undefined;
}

interface FailedRow {
  orderNumber: string;
  line: string;
  sourceRows: string;
  kind: string;
  reason: string;
}

function parse<T>(raw: unknown): T[] {
  if (typeof raw !== 'string' || !raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

/**
 * The failed rows of an order import, read back from the notification payload.
 *
 * The summary email prints the same table and tells the user to open the app
 * for the rest, so the rows have to be reachable here too.
 */
export function OrderImportErrors({ payload }: Props) {
  const { getTranslation: t } = useTranslator({ page: 'notifications' });
  const [open, setOpen] = useState(false);

  const kindLabel = (errors: ClassifiedError[] = []): string =>
    [...new Set(errors.map(e => t(`orderImport.kind.${e.kind}`)))].join(', ');

  const reason = (errors: ClassifiedError[] = []): string =>
    errors.map(e => e.message).join('; ');

  const rows: FailedRow[] = [
    ...parse<OrderError>(payload?.errors).map(error => ({
      orderNumber: error.orderNumber,
      line: '—',
      sourceRows: (error.rowNumbers ?? []).join(', '),
      kind: kindLabel(error.errors),
      reason: reason(error.errors),
    })),
    ...parse<OrderLineError>(payload?.lineErrors).map(error => ({
      orderNumber: error.orderNumber,
      line: error.orderLineNumber ?? error.productCode ?? '—',
      sourceRows: String(error.rowNumber),
      kind: kindLabel(error.errors),
      reason: reason(error.errors),
    })),
  ];

  // Each problem once, with every file row it hits — the same grouping the
  // importer's review step shows, and the one the email leads with.
  const issues = parse<ImportIssue>(payload?.issues);

  if (rows.length === 0) return null;

  const moreCount = Number(payload?.moreErrorsCount ?? 0);

  return (
    <>
      <Button
        variant='link'
        size='sm'
        className='h-auto p-0 mt-1 gap-1 text-xs'
        onClick={() => setOpen(v => !v)}
      >
        {open
          ? t('orderImport.hideFailedRows')
          : t('orderImport.showFailedRows', { count: rows.length })}
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className='size-3'
        />
      </Button>

      {open && issues.length > 0 && (
        <div className='mt-1 overflow-x-auto rounded-md border border-border'>
          <p className='px-2 py-1 text-xs font-medium'>
            {t('orderImport.issuesTitle', { count: issues.length })}
          </p>
          <table className='w-full border-collapse text-xs'>
            <thead>
              <tr className='bg-muted/40 text-muted-foreground'>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.column')}
                </th>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.problem')}
                </th>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.whatToFix')}
                </th>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.fileRows')}
                </th>
              </tr>
            </thead>
            <tbody>
              {issues.map(issue => {
                const hidden = issue.rowCount - issue.rows.length;
                return (
                  <tr
                    key={`${issue.kind}:${issue.column}:${issue.problem}`}
                    className='border-t border-border align-top'
                  >
                    <td className='px-2 py-1 font-medium'>
                      {issue.column || '—'}
                    </td>
                    <td className='px-2 py-1'>{issue.problem}</td>
                    <td className='px-2 py-1 whitespace-nowrap'>
                      {t(`orderImport.kind.${issue.kind}`)}
                    </td>
                    <td className='px-2 py-1'>
                      {issue.rows.join(', ')}
                      {hidden > 0 &&
                        ` ${t('orderImport.moreRows', { count: hidden })}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className='mt-1 overflow-x-auto rounded-md border border-border'>
          <table className='w-full border-collapse text-xs'>
            <thead>
              <tr className='bg-muted/40 text-muted-foreground'>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.order')}
                </th>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.line')}
                </th>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.sourceRow')}
                </th>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.whatToFix')}
                </th>
                <th className='px-2 py-1 text-left font-medium'>
                  {t('orderImport.reason')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={`${row.orderNumber}-${row.sourceRows}-${i}`}
                  className='border-t border-border align-top'
                >
                  <td className='px-2 py-1'>{row.orderNumber}</td>
                  <td className='px-2 py-1'>{row.line}</td>
                  <td className='px-2 py-1 whitespace-nowrap'>
                    {row.sourceRows}
                  </td>
                  <td className='px-2 py-1 whitespace-nowrap'>{row.kind}</td>
                  <td className='px-2 py-1'>{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {moreCount > 0 && (
            <p className='px-2 py-1 text-xs text-muted-foreground'>
              {t('orderImport.andMore', { count: moreCount })}
            </p>
          )}
        </div>
      )}
    </>
  );
}
